/**
 * Owner: Masa Bayu
 * File: author-storefront.ts
 * Date:Time: 2026-09-16 14:25
 * Description: T6-E3 StorefrontConfig — visibility · price hooks · shopper preview handoff.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { assertT6E1SecurityIntact, assertT6E3SecurityIntact } from './author-security';
import { createCommerceOffer } from './commerce-pricing';
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
export const QXREAD_STOREFRONT_VISIBILITY = ['hidden', 'unlisted', 'public_listed'];
function assertVisibility(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_STOREFRONT_VISIBILITY.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_STOREFRONT_VISIBILITY.join(', ')}`);
    }
    return value;
}
function assertMinorUnits(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`${label} must be a non-negative integer`);
    }
    return value;
}
/**
 * Author-facing listing controls. Client price is a hook — checkout recomputes server-side (T3).
 */
export function upsertStorefrontConfig(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead storefront config');
    assertT6E1SecurityIntact();
    assertT6E3SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({ allowed: false, reason: access.reason });
    }
    assertValidId(input.publicationId, 'QXRead publicationId');
    const title = assertOptionalTrimmed(input.title, 'QXRead storefront title');
    if (title === null) {
        throw new Error('QXRead storefront title is required');
    }
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead storefront updatedAt');
    const storefrontConfigId = input.storefrontConfigId !== undefined ? input.storefrontConfigId : createQxreadId('qxrdstfc');
    assertValidId(storefrontConfigId, 'QXRead storefrontConfigId');
    const visibility = assertVisibility(input.visibility ?? 'unlisted', 'QXRead storefront visibility');
    const sellerPriceMinorUnits = assertMinorUnits(input.sellerPriceMinorUnits, 'QXRead sellerPriceMinorUnits');
    const currency = typeof input.currency === 'string' && input.currency.length > 0 ? input.currency : 'USD';
    const promotionalBadgeLabel = assertOptionalTrimmed(input.promotionalBadgeLabel, 'QXRead promotionalBadgeLabel');
    const slug = input.authorAccount.publicProfile.slug;
    const publicProfilePath = `/author/${slug}`;
    const saleableOnQxread = visibility === 'public_listed';
    const offerHint = createCommerceOffer({
        publicationId: input.publicationId,
        qxpPublicationRef: input.qxpPublicationRef ?? null,
        title,
        sellerPriceMinorUnits,
        currency,
        saleableOnQxread,
        marketplaceReady: saleableOnQxread,
    });
    return Object.freeze({
        allowed: true,
        offerHint,
        config: Object.freeze({
            storefrontConfigId,
            authorAccountId: input.authorAccount.authorAccountId,
            accountId: input.authorAccount.accountId,
            publicationId: input.publicationId,
            visibility,
            sellerPriceMinorUnits,
            currency,
            promotionalBadgeLabel,
            publicProfilePath,
            updatedAt,
            mutatesQxp: false,
            mutatesQxw: false,
            mintsEntitlement: false,
        }),
    });
}
/**
 * Preview as shopper — Book Detail read-only chrome. Does not grant entitlement or launch Reader.
 */
export function createStorefrontShopperPreview(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead storefront shopper preview');
    assertT6E3SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({ allowed: false, reason: access.reason });
    }
    if (input.config.authorAccountId !== input.authorAccount.authorAccountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Own-resource scope denied — StorefrontConfig belongs to another AuthorAccount',
        });
    }
    if (input.config.visibility === 'hidden') {
        return Object.freeze({
            allowed: false,
            reason: 'Hidden listings cannot open shopper preview from storefront',
        });
    }
    return Object.freeze({
        allowed: true,
        preview: Object.freeze({
            publicationId: input.config.publicationId,
            shopperPath: `/publication/${input.config.publicationId}`,
            mode: 'book_detail_read_only',
            grantsEntitlement: false,
            executesReader: false,
            reason: 'Storefront shopper preview — Book Detail read-only; checkout recomputes price',
        }),
    });
}
//# sourceMappingURL=author-storefront.js.map